// BacktestPage — Backtesting Lab (Pro tier)
// Layout:
//   - Top nav: < Back | "Backtesting"
//   - Hero: tier-gating
//   - Strategy picker
//   - Time range + capital + risk
//   - Run button
//   - Results: equity curve, stats, trade list

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlatformLogo } from '../../components/Brand';
import {
  TfCard,
  TfCardList,
  TfSectionHeaderView,
  TfSparklineMarker,
  TfTimeRange,
} from '../../components/TfPrimitives';

interface BacktestResult {
  id: string;
  startDate: string;
  endDate: string;
  duration: string;
  totalReturn: number;
  cagr: number;
  sharpe: number;
  maxDD: number;
  winRate: number;
  trades: number;
  avgWin: number;
  avgLoss: number;
  profitFactor: number;
}

const RESULT: BacktestResult = {
  id: 'r1',
  startDate: '2025-01-01', endDate: '2025-12-31',
  duration: '12 months',
  totalReturn: 78.4, cagr: 78.4, sharpe: 2.14, maxDD: -8.2,
  winRate: 67.0, trades: 412,
  avgWin: 184.0, avgLoss: -98.0, profitFactor: 2.13,
};

const STRATEGIES = [
  { key: 'breakout',     name: 'Breakout',     locked: false, color: '#34C759', icon: '🚀' },
  { key: 'mean-revert',  name: 'Mean Reversion', locked: true,  color: '#007AFF', icon: '🔁' },
  { key: 'trend',        name: 'Trend Follow', locked: true,  color: '#FF9500', icon: '📈' },
  { key: 'sniper',       name: 'Sniper',       locked: true,  color: '#FF3B30', icon: '🎯' },
];

const RANGES = ['1M', '3M', '6M', '1Y', '2Y', '5Y'] as const;

export function BacktestPage() {
  const nav = useNavigate();
  const [strategy, setStrategy] = useState('breakout');
  const [range, setRange] = useState<typeof RANGES[number]>('1Y');
  const [running, setRunning] = useState(false);
  const [hasResult, setHasResult] = useState(true);

  function run() {
    setRunning(true);
    setHasResult(false);
    setTimeout(() => { setRunning(false); setHasResult(true); }, 1800);
  }

  // Equity curve
  const equity = (() => {
    const n = 100;
    const out: number[] = [];
    let v = 50000;
    for (let i = 0; i < n; i++) {
      v += (Math.sin(i * 0.15) * 800) + (Math.random() - 0.45) * 1200;
      out.push(v);
    }
    return out;
  })();
  const finalPct = ((equity[equity.length - 1] - equity[0]) / equity[0]) * 100;

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-tf-bg/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold text-tf-text-1">Backtest lab</div>
        <div className="w-9" />
      </div>

      {/* Hero — strategy picker */}
      <div className="mx-4 mt-4">
        <TfCard>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Strategy</div>
              <div className="text-[24px] font-bold text-tf-text-1">{STRATEGIES.find((s) => s.key === strategy)?.name || '—'}</div>
            </div>
            <div
              className="flex h-12 w-12 items-center justify-center rounded-[14px] text-[24px]"
              style={{ backgroundColor: (STRATEGIES.find((s) => s.key === strategy)?.color || '#34C759') + '18' }}
            >
              {STRATEGIES.find((s) => s.key === strategy)?.icon}
            </div>
          </div>
          {/* Strategy selector */}
          <div className="mt-3 grid grid-cols-2 gap-2">
            {STRATEGIES.map((s) => (
              <button
                key={s.key}
                onClick={() => !s.locked && setStrategy(s.key)}
                disabled={s.locked}
                data-testid={`strategy-${s.key}`}
                className={['flex items-center gap-2 rounded-[12px] p-2.5 transition',
                  strategy === s.key ? 'bg-tf-text-1 text-white' : 'bg-tf-bg border border-tf-border text-tf-text-1',
                  s.locked ? 'opacity-50' : 'active:scale-95'
                ].join(' ')}
              >
                <span className="text-[18px]">{s.icon}</span>
                <div className="flex-1 text-left">
                  <div className="text-[12px] font-semibold">{s.name}</div>
                  {s.locked && <div className="text-[9px] uppercase font-bold text-tf-warning">🔒 Pro</div>}
                </div>
              </button>
            ))}
          </div>
        </TfCard>
      </div>

      {/* Settings */}
      <TfSectionHeaderView title="Backtest settings" />
      <div className="mx-4">
        <TfCardList>
          <div className="px-4 py-3 border-b border-tf-border">
            <div className="text-[13px] font-semibold text-tf-text-1">Time range</div>
            <div className="mt-2 grid grid-cols-6 gap-1.5">
              {RANGES.map((r) => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  data-testid={`range-${r}`}
                  className={['h-8 rounded-[8px] text-[12px] font-semibold transition',
                    range === r ? 'bg-tf-text-1 text-white' : 'bg-tf-surface-1 text-tf-text-1'].join(' ')}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 border-b border-tf-border">
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-profit/15 text-[16px]">💰</div>
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-tf-text-1">Initial capital</div>
              <div className="text-[11px] text-tf-text-3">$50,000</div>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 border-b border-tf-border">
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-loss/15 text-[16px]">⚠️</div>
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-tf-text-1">Risk per trade</div>
              <div className="text-[11px] text-tf-text-3">1.0% of equity</div>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 border-b border-tf-border">
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-blue/15 text-[16px]">📊</div>
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-tf-text-1">Instruments</div>
              <div className="mt-1 flex gap-1">
                <PlatformLogo name="ninjatrader" size={20} />
                <PlatformLogo name="rithmic" size={20} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-orange/15 text-[16px]">📉</div>
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-tf-text-1">Slippage</div>
              <div className="text-[11px] text-tf-text-3">0.5 ticks</div>
            </div>
          </div>
        </TfCardList>
      </div>

      {/* Run button */}
      <div className="mx-4 mt-4">
        <button
          onClick={run}
          disabled={running}
          data-testid="run-backtest"
          className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-tf-text-1 text-white text-[16px] font-semibold disabled:opacity-50 active:scale-[0.99]"
        >
          {running ? (
            <>
              <svg viewBox="0 0 24 24" className="h-5 w-5 animate-spin" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" /></svg>
              Running backtest…
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 3l14 9-14 9V3z" strokeLinecap="round" strokeLinejoin="round" /></svg>
              Run backtest
            </>
          )}
        </button>
      </div>

      {/* Result */}
      {hasResult && (
        <>
          <TfSectionHeaderView title="Equity curve" viewAllLabel="Compare" onViewAll={() => alert('Compare runs')} />
          <TfCard className="mx-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[11px] text-tf-text-3 uppercase">Total return</div>
                <div className="text-[24px] font-bold text-tf-text-1 tabular-nums">+{RESULT.totalReturn.toFixed(1)}%</div>
              </div>
              <div className="text-right">
                <div className="text-[11px] text-tf-text-3 uppercase">$50K →</div>
                <div className="text-[18px] font-bold tabular-nums text-tf-profit">$89,200</div>
              </div>
            </div>
            <div className="-mx-1">
              <TfSparklineMarker
                points={equity}
                color="#34C759"
                height={140}
                markerIndex={Math.floor(equity.length * 0.6)}
                markerLabel={`$${(equity[Math.floor(equity.length * 0.6)] / 1000).toFixed(1)}K`}
                markerPct={((equity[Math.floor(equity.length * 0.6)] - equity[0]) / equity[0]) * 100}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] text-tf-text-3">
              <span>Jan 2025</span>
              <span>Apr</span>
              <span>Aug</span>
              <span>Dec 2025</span>
            </div>
            <TfTimeRange value={range} onChange={setRange} options={['1D', '1W', '1M', '3M', '6M', '1Y']} />
          </TfCard>

          {/* Stats */}
          <TfSectionHeaderView title="Performance" />
          <div className="mx-4 grid grid-cols-2 gap-2">
            <Stat label="CAGR" value={`+${RESULT.cagr.toFixed(1)}%`} color="text-tf-profit" />
            <Stat label="Sharpe" value={RESULT.sharpe.toFixed(2)} color="text-tf-blue" />
            <Stat label="Win rate" value={`${RESULT.winRate.toFixed(1)}%`} color="text-tf-profit" />
            <Stat label="Max DD" value={`${RESULT.maxDD.toFixed(1)}%`} color="text-tf-loss" />
            <Stat label="Trades" value={RESULT.trades.toString()} color="text-tf-text-1" />
            <Stat label="Profit factor" value={RESULT.profitFactor.toFixed(2)} color="text-tf-profit" />
            <Stat label="Avg win" value={`+$${RESULT.avgWin.toFixed(0)}`} color="text-tf-profit" />
            <Stat label="Avg loss" value={`-$${Math.abs(RESULT.avgLoss).toFixed(0)}`} color="text-tf-loss" />
          </div>

          {/* Sample trades */}
          <TfSectionHeaderView title="Top trades" viewAllLabel="All" onViewAll={() => alert('View all trades')} />
          <div className="mx-4">
            <TfCardList>
              {[
                { p: 'NQ', d: '2025-08-15 09:32', pnl: +420, dir: 'long' },
                { p: 'ES', d: '2025-09-12 14:15', pnl: +380, dir: 'long' },
                { p: 'CL', d: '2025-10-04 11:00', pnl: -185, dir: 'short' },
                { p: 'ES', d: '2025-11-22 10:45', pnl: +340, dir: 'short' },
              ].map((t, i, a) => (
                <div
                  key={i}
                  data-testid={`bt-trade-${i}`}
                  className={['flex items-center gap-3 px-4 py-3', i < a.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-surface-1 text-[10px] font-bold text-tf-text-1">{t.p}</div>
                  <div className="flex-1">
                    <div className="text-[13px] font-semibold text-tf-text-1">{t.p} · {t.dir}</div>
                    <div className="text-[11px] text-tf-text-3">{t.d}</div>
                  </div>
                  <div className={['text-[14px] font-bold tabular-nums', t.pnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                    {t.pnl >= 0 ? '+' : ''}${Math.abs(t.pnl)}
                  </div>
                </div>
              ))}
            </TfCardList>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="rounded-[12px] border border-tf-border bg-tf-bg p-3">
      <div className="text-[11px] uppercase tracking-wide text-tf-text-3">{label}</div>
      <div className={['mt-1 text-[20px] font-bold tabular-nums', color].join(' ')}>{value}</div>
    </div>
  );
}
